import React from "react";
import { Check, Clock, CreditCard, MapPin, ShieldCheck, Zap } from "lucide-react";

const JOURNEY_STEPS = [
  { label: "ARRIVE", title: "ARRIVE", role: "Airport", color: "#717182", muted: true },
  { label: "MOVE", title: "MOVE", role: "Rental Car", color: "#017bef" },
  { label: "WORK", title: "WORK", role: "Meeting / Workspace", color: "#0062c0" },
  { label: "STAY", title: "STAY", role: "Hotel / Apartment", color: "#0ea5e9" },
  { label: "PLAY", title: "PLAY", role: "Weekend Adventure", color: "#6366f1" },
];

export function MoveEcosystem() {
  return (
    <section className="bg-white py-20">
      <div className="mx-auto flex max-w-[1440px] flex-col items-center px-[16px] text-center sm:px-8">
        <p className="text-[14px] font-semibold uppercase leading-5 tracking-[1.4px] text-[#017bef]">The Ecosystem</p>
        <h2 className="pt-[12px] text-[36px] font-bold leading-[1.2] tracking-[-1.2px] text-[#0a0a0a] sm:text-[48px] sm:leading-[48px]">
          Move With Your Journey
        </h2>
        <p className="max-w-[576px] pt-[16px] text-[18px] leading-7 text-[#717182]">
          Move brings car rental onto the SPACIA platform, connecting you with accredited rental companies for every
          stage of your journey.
        </p>

        <div className="grid w-full max-w-[896px] grid-cols-2 gap-y-10 pt-14 sm:grid-cols-5">
          {JOURNEY_STEPS.map((step, index) => (
            <div key={step.label} className="relative flex flex-col items-center px-[12px]">
              {/* Dashed link to the next circle (desktop only) */}
              {index < JOURNEY_STEPS.length - 1 && (
                <div className="absolute left-[calc(50%+28px)] top-[27px] hidden w-[calc(100%-56px)] border-t-2 border-dashed border-[#e5e7eb] sm:block" />
              )}
              <div className={`flex flex-col items-center ${step.muted ? "opacity-50" : ""}`}>
                <div
                  className="mb-[16px] flex size-14 items-center justify-center rounded-full border-4 border-solid border-white text-[10px] font-bold leading-[15px] tracking-[1px] text-white drop-shadow-[0_4px_8px_rgba(0,0,0,0.1)]"
                  style={{ backgroundColor: step.color }}
                >
                  {step.label}
                </div>
                <p className="pb-1 text-[14px] font-bold leading-5 text-[#0a0a0a]">{step.title}</p>
              </div>
              <p className="text-[12px] leading-[19.5px] text-[#717182]">{step.role}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const REASONS = [
  { icon: ShieldCheck, title: "Book with confidence", description: "Accredited, verified rental providers only." },
  { icon: Zap, title: "Move seamlessly", description: "Connect car rental with your Work, Stay and Play plans." },
  { icon: CreditCard, title: "Transparent pricing", description: "No hidden surprises. Know what you're paying for." },
  {
    icon: Clock,
    title: "Flexible rental durations",
    description: "Daily, weekly or long-term — whatever your trip requires.",
  },
  {
    icon: MapPin,
    title: "Flexible pickup and drop-off",
    description: "Your journey doesn't have to start and end in the same place.",
  },
];

export function MoveWhy() {
  return (
    <section className="bg-[#f8fbff] py-20">
      <div className="mx-auto grid max-w-[1440px] grid-cols-1 items-center gap-16 px-[16px] sm:px-8 lg:grid-cols-2">
        <div>
          <p className="text-[14px] font-semibold uppercase leading-5 tracking-[1.4px] text-[#017bef]">Why Move</p>
          <h2 className="pt-[12px] text-[36px] font-bold leading-[1.2] tracking-[-1.2px] text-[#0a0a0a] sm:text-[48px] sm:leading-[48px]">
            More Freedom to Move
          </h2>
          <p className="pt-[20px] text-[18px] leading-[29.25px] text-[#717182]">
            Move seamlessly between your Work, Stay and Play plans — with a vehicle that fits your journey, booked
            through one platform.
          </p>
          <ul className="space-y-[20px] pt-8">
            {REASONS.map(({ icon: Icon, title, description }) => (
              <li key={title} className="flex items-start gap-[16px]">
                <span className="mt-0.5 flex size-10 shrink-0 items-center justify-center rounded-[14px] bg-[#017bef]/[0.08]">
                  <Icon className="size-[18px] text-[#017bef]" />
                </span>
                <div>
                  <p className="text-[14px] font-bold leading-5 text-[#0a0a0a]">{title}</p>
                  <p className="pt-0.5 text-[14px] leading-[22.75px] text-[#717182]">{description}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="relative h-[360px] overflow-hidden rounded-3xl sm:h-[492px]">
          <img
            src="/figma-assets/move/why.jpg"
            alt="Rental car on the road"
            className="absolute inset-0 size-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
          <div className="absolute inset-x-[20px] bottom-[20px] flex items-center justify-between rounded-2xl border-[1px] border-solid border-white/60 bg-white/95 px-[16px] py-[12px] shadow-[0_4px_6px_0_rgba(0,0,0,0.1),0_2px_4px_0_rgba(0,0,0,0.1)]">
            <span className="flex items-center gap-2 text-[12px] font-semibold leading-4 text-[#0a0a0a]">
              <span className="size-2 rounded-full bg-[#22c55e]" />
              Accredited provider
            </span>
            <span className="text-[12px] leading-4 text-[#717182]">Verified</span>
          </div>
        </div>
      </div>
    </section>
  );
}

const VEHICLES = [
  {
    key: "economy",
    name: "Economy",
    image: "/figma-assets/move/economy.jpg",
    badgeClass: "bg-[#017bef]",
    shade: "bg-gradient-to-t from-[rgba(10,10,10,0.8)] via-[rgba(10,10,10,0.2)] via-95% to-transparent",
    description: "For practical, flexible everyday travel. Reliable vehicles from accredited providers.",
    points: [
      "Practical and cost-effective",
      "Ideal for city and business travel",
      "Flexible daily and weekly rental",
      "Accredited providers",
    ],
  },
  {
    key: "executive",
    name: "Executive",
    image: "/figma-assets/move/executive.jpg",
    badgeClass: "border-[1px] border-solid border-white/30 bg-white/20",
    shade: "bg-gradient-to-t from-[#0a0a0a] via-[rgba(0,0,0,0.3)] via-65% to-transparent",
    description: "For a more premium travel experience, from airport arrivals to client engagements.",
    points: [
      "Premium vehicle experience",
      "Ideal for client meetings and offsites",
      "Long-term rental available",
      "Verified, accredited providers",
    ],
  },
];

export function MoveVehicles() {
  return (
    <section className="bg-white py-20">
      <div className="mx-auto max-w-[1440px] px-[16px] sm:px-8">
        <p className="text-center text-[14px] font-semibold uppercase leading-5 tracking-[1.4px] text-[#017bef]">
          Vehicle Choice
        </p>
        <h2 className="pt-[12px] text-center text-[36px] font-bold leading-[1.2] tracking-[-1.2px] text-[#0a0a0a] sm:text-[48px] sm:leading-[48px]">
          Choose the Right Vehicle for Your Journey
        </h2>

        <div className="grid grid-cols-1 gap-6 pt-14 lg:grid-cols-2">
          {VEHICLES.map(vehicle => (
            <div key={vehicle.key} className="relative flex min-h-[380px] items-end overflow-hidden rounded-3xl">
              <img src={vehicle.image} alt={vehicle.name} className="absolute inset-0 size-full object-cover" />
              <div className={`absolute inset-0 ${vehicle.shade}`} />
              <div className="relative p-8">
                <span
                  className={`inline-block rounded-full px-[12px] py-1 text-[10px] font-bold uppercase leading-[15px] tracking-[1px] text-white ${vehicle.badgeClass}`}
                >
                  {vehicle.name}
                </span>
                <h3 className="pt-[16px] text-[24px] font-bold leading-8 text-white">{vehicle.name}</h3>
                <p className="pt-[12px] text-[14px] leading-[22.75px] text-white">{vehicle.description}</p>
                <ul className="space-y-1.5 pt-[20px]">
                  {vehicle.points.map(point => (
                    <li key={point} className="flex items-center gap-2 text-[14px] leading-5 text-white">
                      <Check className="size-[11px] shrink-0 text-[#60a5fa]" />
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
